import { useMemo } from "react";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Dimmer, Loader } from "semantic-ui-react";

import {
  BaseTableV3Body,
  MemoizedBaseTableV3Body,
} from "components/BaseTableV3/BaseTableV3Body";
import { BaseTableV3Header } from "components/BaseTableV3/BaseTableV3Header";
import { MessageSnack } from "components/BaseTableV3/MessageSnack";
import styles from "components/BaseTableV3/BaseTableV3.module.scss";

export function ResponsiveTable({
  table,
  columnOrder,
  isFetched,
  isFetching,
  isError,
  queryResult,
  columnSizeVars,
  handleResizeColumnQuery,
}) {
  const hasData = queryResult?.data?.length > 0;
  const showInitialLoader = !isFetched && !hasData && !isError;
  const dimmed = isFetching && hasData;

  const tableStyle = useMemo(
    () => ({
      ...columnSizeVars,
      opacity: dimmed ? 0.6 : 1,
      transition: "opacity 0.15s ease-in-out",
    }),
    [columnSizeVars, dimmed]
  );

  return (
    <div className={styles["table-responsive-wrapper"]}>
      {isFetched && !hasData && !isError ? (
        <MessageSnack
          message="No records found. Try adjusting your filters."
          messageType="warning"
        />
      ) : null}
      {isError ? (
        <MessageSnack
          message={"Error fetching data. Please try again."}
          messageType="error"
        />
      ) : null}
      {showInitialLoader ? (
        <Dimmer active inverted className={styles["loading-dimmer"]}>
          <Loader inverted>Loading</Loader>
        </Dimmer>
      ) : null}
      <table style={tableStyle}>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              <SortableContext
                items={columnOrder}
                strategy={horizontalListSortingStrategy}
              >
                {headerGroup.headers.map(header => (
                  <BaseTableV3Header
                    key={header.id}
                    header={header}
                    handleResizeColumnQuery={handleResizeColumnQuery}
                  />
                ))}
              </SortableContext>
            </tr>
          ))}
        </thead>

        {table.getState().columnSizingInfo.isResizingColumn ? (
          <MemoizedBaseTableV3Body table={table} />
        ) : (
          <BaseTableV3Body table={table} />
        )}
      </table>
    </div>
  );
}
